import React, { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  Search,
  Eye,
  Ban,
  CheckCircle,
  Shield,
  Users,
  UserX,
  FileCheck,
  Plus,
  Trash2,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import adminService from '../../services/adminService';
import kycService from '../../services/kycService';
import { useAuth } from '../../context/AuthContext';
import Can from '../../components/auth/Can';
import UserDetailsModal from '../../components/admin/users/UserDetailsModal';
import { ASSIGNABLE_ROLES, PERMISSIONS, ROLE_LABELS } from '../../utils/rbac';

const TABS = [
  { id: 'all', label: 'Tous les utilisateurs', icon: Users },
  { id: 'kyc-pending', label: 'Validations KYC', icon: FileCheck },
  { id: 'suspended', label: 'Comptes suspendus', icon: UserX },
];

const StatusBadge = ({ status }) => {
  const active = !status || status === 'ACTIVE';
  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
        active
          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
          : 'bg-red-100 text-red-800 border border-red-200'
      }`}
    >
      {active ? 'Actif' : 'Suspendu'}
    </span>
  );
};

const RoleBadge = ({ role }) => (
  <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">
    {ROLE_LABELS[role] || role || '—'}
  </span>
);

/**
 * Page admin de gestion des utilisateurs (RBAC) :
 * onglets Tous / KYC / Suspendus + édition rôle / statut.
 */
const UsersManagementPage = () => {
  const { user: currentUser, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';

  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.id === initialTab) ? initialTab : 'all'
  );
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsListUser, setDetailsListUser] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [createForm, setCreateForm] = useState({
    fullName: '',
    email: '',
    password: '',
    role: 'user',
  });

  const canAssignSuperAdmin = hasRole('super_admin');
  const assignableRoles = ASSIGNABLE_ROLES.filter(
    (r) => r !== 'super_admin' || canAssignSuperAdmin
  );
  const creatableRoles = assignableRoles.filter((r) => r !== 'super_admin' || canAssignSuperAdmin);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'kyc-pending') {
        const response = await adminService.getUsersWithKYC({
          page: currentPage,
          limit: 20,
          search: searchTerm,
          kycStatus: 'PENDING',
        });
        setUsers(response.data || []);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalItems: (response.data || []).length,
            itemsPerPage: 20,
          }
        );
      } else {
        const response = await adminService.getUsers({
          page: currentPage,
          limit: 20,
          search: searchTerm || undefined,
          role: roleFilter || undefined,
          accountStatus: activeTab === 'suspended' ? 'SUSPENDED' : undefined,
        });
        setUsers(response.data || []);
        setPagination(
          response.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalItems: (response.data || []).length,
            itemsPerPage: 20,
          }
        );
      }
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erreur lors du chargement des utilisateurs');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, currentPage, searchTerm, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setCurrentPage(1);
    const next = { tab: activeTab };
    const userId = searchParams.get('userId');
    if (userId) next.userId = userId;
    setSearchParams(next);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadUsers();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserDetails = useCallback(async (userId, listRow = null) => {
    setIsLoadingDetails(true);
    setDetailsData(null);
    try {
      const response = await adminService.getUserDetails(userId);
      if (response.success && response.data) {
        setDetailsData(response.data);
        if (response.data.user) {
          setDetailsListUser(response.data.user);
        } else if (listRow) {
          setDetailsListUser(listRow);
        }
      } else if (listRow) {
        setDetailsListUser(listRow);
      }
    } catch (error) {
      alert(error.message || 'Erreur lors du chargement des détails');
      setDetailsOpen(false);
    } finally {
      setIsLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    const userId = searchParams.get('userId');
    if (!userId || detailsOpen) return;
    setDetailsListUser({ _id: userId });
    setDetailsOpen(true);
    fetchUserDetails(userId);
  }, [searchParams, detailsOpen, fetchUserDetails]);

  const closeDetailsModal = () => {
    setDetailsOpen(false);
    setDetailsData(null);
    setDetailsListUser(null);
    if (searchParams.get('userId')) {
      const next = { tab: activeTab };
      setSearchParams(next);
    }
  };

  const openRoleModal = (u) => {
    setSelectedUser(u);
    setNewRole(u.role || 'user');
    setRoleModalOpen(true);
  };

  const openStatusModal = (u) => {
    setSelectedUser(u);
    setSuspendReason('');
    setStatusModalOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    setIsProcessing(true);
    try {
      await adminService.updateUserRole(selectedUser._id, newRole);
      setRoleModalOpen(false);
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur lors du changement de rôle');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    const isSuspended = selectedUser.accountStatus === 'SUSPENDED';
    const nextStatus = isSuspended ? 'ACTIVE' : 'SUSPENDED';
    if (nextStatus === 'SUSPENDED' && !suspendReason.trim()) {
      alert('Veuillez saisir un motif de suspension');
      return;
    }
    setIsProcessing(true);
    try {
      await adminService.updateUserStatus(
        selectedUser._id,
        nextStatus,
        nextStatus === 'SUSPENDED' ? suspendReason.trim() : undefined
      );
      setStatusModalOpen(false);
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifyKYC = async (userId) => {
    if (!window.confirm('Valider le KYC de cet utilisateur ?')) return;
    setIsProcessing(true);
    try {
      await kycService.verifyKYC(userId);
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur KYC');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewDetails = (u) => {
    setDetailsListUser(u);
    setDetailsOpen(true);
    fetchUserDetails(u._id, u);
  };

  const handleVerifyContact = async (field) => {
    const userId = detailsData?.user?._id || detailsListUser?._id;
    if (!userId) return;
    const fieldLabel = field === 'email' ? 'e-mail' : 'téléphone';
    if (!window.confirm(`Marquer le ${fieldLabel} comme vérifié ?`)) return;
    setIsProcessing(true);
    try {
      const response = await adminService.verifyUserContact(userId, field, true);
      setDetailsData((prev) =>
        prev
          ? {
              ...prev,
              user: {
                ...prev.user,
                emailVerified: response.data?.emailVerified ?? prev.user?.emailVerified,
                phoneVerified: response.data?.phoneVerified ?? prev.user?.phoneVerified,
                emailVerifiedAt: response.data?.emailVerifiedAt ?? prev.user?.emailVerifiedAt,
                phoneVerifiedAt: response.data?.phoneVerifiedAt ?? prev.user?.phoneVerifiedAt,
                email_verified_at: response.data?.email_verified_at ?? prev.user?.email_verified_at,
                phone_verified_at: response.data?.phone_verified_at ?? prev.user?.phone_verified_at,
              },
            }
          : prev
      );
    } catch (error) {
      alert(error.message || 'Erreur lors de la vérification');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifyKYCFromModal = async (userId) => {
    if (!window.confirm('Valider le KYC de cet utilisateur ?')) return;
    setIsProcessing(true);
    try {
      await kycService.verifyKYC(userId);
      await fetchUserDetails(userId, detailsListUser);
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur KYC');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectKYCFromModal = async (userId) => {
    const reason = window.prompt('Raison du rejet du KYC :');
    if (!reason?.trim()) return;
    setIsProcessing(true);
    try {
      await kycService.rejectKYC(userId, reason.trim());
      await fetchUserDetails(userId, detailsListUser);
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur lors du rejet KYC');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email.trim() || !createForm.password || !createForm.fullName.trim()) {
      alert('Nom, email et mot de passe sont requis');
      return;
    }
    if (createForm.password.length < 6) {
      alert('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setIsProcessing(true);
    try {
      const nameParts = createForm.fullName.trim().split(/\s+/);
      await adminService.createUser({
        fullName: createForm.fullName.trim(),
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' ') || nameParts[0],
        email: createForm.email.trim(),
        password: createForm.password,
        role: createForm.role,
      });
      setCreateModalOpen(false);
      setCreateForm({ fullName: '', email: '', password: '', role: 'user' });
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur lors de la création');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteUser = async (u) => {
    if (isSelf(u)) {
      alert('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    if (u.role === 'super_admin') {
      alert('Impossible de supprimer un super_admin');
      return;
    }
    const label = u.fullName || u.email;
    if (!window.confirm(`Supprimer définitivement « ${label} » ? Cette action est irréversible.`)) {
      return;
    }
    setIsProcessing(true);
    try {
      await adminService.deleteUser(u._id);
      await loadUsers();
    } catch (error) {
      alert(error.message || 'Erreur lors de la suppression');
    } finally {
      setIsProcessing(false);
    }
  };

  const isSelf = (u) => currentUser?._id === u._id || currentUser?.id === u._id;

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des utilisateurs</h1>
            <p className="text-sm text-gray-500 mt-1">
              Rôles, statuts de compte et validations KYC
            </p>
          </div>
          <Can permission={PERMISSIONS.USERS_WRITE}>
            <button
              type="button"
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              <Plus size={16} />
              Créer un utilisateur
            </button>
          </Can>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher (nom, email, téléphone…)"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          {activeTab === 'all' && (
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Tous les rôles</option>
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {isLoading ? (
            <p className="p-6 text-gray-500">Chargement...</p>
          ) : users.length === 0 ? (
            <p className="p-6 text-gray-500">Aucun utilisateur trouvé.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">
                      Utilisateur
                    </th>
                    <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">
                      Rôle
                    </th>
                    <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">
                      Statut
                    </th>
                    <th className="text-left text-xs font-semibold uppercase text-gray-500 px-4 py-3">
                      KYC
                    </th>
                    <th className="text-right text-xs font-semibold uppercase text-gray-500 px-4 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id} className="border-b border-gray-50 hover:bg-gray-50/80">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'}
                        </div>
                        <div className="text-sm text-gray-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={u.accountStatus} />
                        {u.accountStatus === 'SUSPENDED' && u.suspendedReason ? (
                          <p className="text-xs text-red-600 mt-1 max-w-xs truncate" title={u.suspendedReason}>
                            {u.suspendedReason}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {u.kycStatus || 'NONE'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handleViewDetails(u)}
                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                            title="Détails"
                          >
                            <Eye size={16} />
                          </button>
                          <Can permission={PERMISSIONS.USERS_ROLE}>
                            {!isSelf(u) && (
                              <button
                                type="button"
                                onClick={() => openRoleModal(u)}
                                className="p-2 rounded-lg border border-gray-200 hover:bg-blue-50 text-blue-700"
                                title="Modifier le rôle"
                              >
                                <Shield size={16} />
                              </button>
                            )}
                          </Can>
                          <Can permission={PERMISSIONS.USERS_SUSPEND}>
                            {!isSelf(u) && (
                              <button
                                type="button"
                                onClick={() => openStatusModal(u)}
                                className={`p-2 rounded-lg border ${
                                  u.accountStatus === 'SUSPENDED'
                                    ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                    : 'border-red-200 text-red-700 hover:bg-red-50'
                                }`}
                                title={
                                  u.accountStatus === 'SUSPENDED' ? 'Réactiver' : 'Suspendre'
                                }
                              >
                                {u.accountStatus === 'SUSPENDED' ? (
                                  <CheckCircle size={16} />
                                ) : (
                                  <Ban size={16} />
                                )}
                              </button>
                            )}
                          </Can>
                          <Can permission={PERMISSIONS.KYC_VERIFY}>
                            {u.kycStatus === 'PENDING' && (
                              <button
                                type="button"
                                disabled={isProcessing}
                                onClick={() => handleVerifyKYC(u._id)}
                                className="px-2 py-1 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                              >
                                Valider KYC
                              </button>
                            )}
                          </Can>
                          <Can permission={PERMISSIONS.USERS_WRITE}>
                            {!isSelf(u) && u.role !== 'super_admin' && (
                              <button
                                type="button"
                                disabled={isProcessing}
                                onClick={() => handleDeleteUser(u)}
                                className="p-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                                title="Supprimer"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </Can>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex justify-between items-center">
          <button
            type="button"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            className="px-4 py-2 bg-white border rounded-lg text-sm disabled:opacity-50"
          >
            Précédent
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.currentPage || currentPage} / {pagination.totalPages || 1}
            {typeof pagination.totalItems === 'number' ? ` · ${pagination.totalItems} entrées` : ''}
          </span>
          <button
            type="button"
            disabled={currentPage >= (pagination.totalPages || 1)}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-4 py-2 bg-white border rounded-lg text-sm disabled:opacity-50"
          >
            Suivant
          </button>
        </div>

        {/* Create user modal */}
        {createModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold mb-4">Créer un utilisateur</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Nom complet</label>
                  <input
                    type="text"
                    value={createForm.fullName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, fullName: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="jean@exemple.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Mot de passe</label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    placeholder="Minimum 6 caractères"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Rôle</label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  >
                    {creatableRoles.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="px-4 py-2 text-sm rounded-lg border"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleCreateUser}
                  className="px-4 py-2 text-sm rounded-lg bg-slate-900 text-white disabled:opacity-50"
                >
                  Créer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Role modal */}
        {roleModalOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold mb-2">Modifier le rôle</h2>
              <p className="text-sm text-gray-500 mb-4">
                {selectedUser.fullName || selectedUser.email}
              </p>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4"
              >
                {assignableRoles.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRoleModalOpen(false)}
                  className="px-4 py-2 text-sm rounded-lg border"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleUpdateRole}
                  className="px-4 py-2 text-sm rounded-lg bg-slate-900 text-white disabled:opacity-50"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status modal */}
        {statusModalOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold mb-2">
                {selectedUser.accountStatus === 'SUSPENDED'
                  ? 'Réactiver le compte'
                  : 'Suspendre le compte'}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {selectedUser.fullName || selectedUser.email}
              </p>
              {selectedUser.accountStatus !== 'SUSPENDED' && (
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  rows={3}
                  placeholder="Motif de suspension (obligatoire)"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4"
                />
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setStatusModalOpen(false)}
                  className="px-4 py-2 text-sm rounded-lg border"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={handleToggleStatus}
                  className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 ${
                    selectedUser.accountStatus === 'SUSPENDED'
                      ? 'bg-emerald-600'
                      : 'bg-red-600'
                  }`}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}

        <UserDetailsModal
          key={detailsListUser?._id || 'details'}
          open={detailsOpen && Boolean(detailsListUser)}
          onClose={closeDetailsModal}
          detailsData={detailsData}
          listUser={detailsListUser}
          isLoading={isLoadingDetails}
          isProcessing={isProcessing}
          onVerifyContact={handleVerifyContact}
          onVerifyKYC={handleVerifyKYCFromModal}
          onRejectKYC={handleRejectKYCFromModal}
        />
      </div>
    </DashboardLayout>
  );
};

export default UsersManagementPage;
